import { createAction } from 'redux-actions'; // Helpers that the function below needs
// TODO: Move capitalize to a pantheon utils folder

var capitalize = function capitalize(str) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}; // Takes a given action type and returns camel cased version that can be used
// as the name for an action function. eg:
// GET_ARTICLES => getArticles


var formatActionName = function formatActionName(type) {
  return type.split('_').map(function (token) {
    return token.toLowerCase();
  }).reduce(function (name, token, i) {
    return i === 0 ? token : "" + name + capitalize(token);
  }, '');
};
/**
 * Creates a set of 4 actions that help a developer using Pantheons `httpMiddleware` easily make an http request to an API. 
 * 
 * Each action is named using the `type` param you pass to `createHttpAction` and a standard suffix (`Start`,`Success`, `Failure`, or `WithOptions`):
 * 
 * ```js
 * import { createHttpAction } from '@minerva-analytics/pantheon';
 *
 * const {
 *   getArticlesStart, // has the type HTTP_GET_ARTICLES
 *   getArticlesSuccess, // has the type GET_ARTICLES_SUCCESS
 *   getArticlesFailure, // has the type GET_ARTICLES_FAILURE
 *    
 *   // dispatches `getArticlesStart` with the correct payload
 *   getArticlesWithOptions,
 * } = createHttpAction('GET_ARTICLES');
 * ```
 * 
 * To use these actions correctly, you should only ever need to dispatch `getArticlesWithOptions`. The other actions should only need to be used as keys for [`handleActions`](https://redux-actions.js.org/api/handleaction#handleactions).
 * 
 * The httpMiddleware will handle dispatching `getArticlesSuccess` or `getArticlesFailure` depending on whether the request was successful or not.
 * 
 * All `WithOptions` actions take an [axios request config](https://github.com/axios/axios#request-config) as the sole argument. 
 * 
 * Note that you _must_ provide `method` and `url` in this request config:
 *
 * ```js
 * dispatch(getArticleWithOptions({
 *   method: 'get',
 *   url: 'https://api-lucina.com/articles/',
 * }));
 * ```
 *
 * It's recomended that you create wrappers around any `WithOptions` actions to make dispatching them in your code easier:
 *
 * ```js
 * const getArticles = () => getArticleWithOptions({
 *   method: 'get',
 *   url: 'https://api-lucina.com/articles/',
 * });
 * dispatch(getArticles());
 * ```
 * 
 * The default success and failure actions are configured to work with any valid response from DRF by default. However, you can also customize what the `OnSuccess` and `OnFailure` actions creators are when calling `createHttpAction`. 
 * 
 * This can be useful if you need to format API response data:
 * 
 * ```js
 * import { createHttpAction } from '@minerva-analytics/pantheon';
 *
 * const {
 *   // This is your custom onSuccess function but with a couple tweaks to
 *   // allow it to be used with `handleActions` from redux-actions
 *   getArticlesSuccess,

 *   // This is your custom onFailure function with the same tweaks made as
 *   // `getArticlesSuccess
 *   getArticlesFailure, // has the type GET_ARTICLES_FAILURE
 * } = createHttpAction('GET_ARTICLES', {
 *   onSuccess: (response) => {
 *     // You MUST return an action with the same type as the default action
 *     // from a custom onSuccess callback
 *     return {
 *       type: 'GET_ARTICLES_SUCCESS',
 *     }
 *   },
 *   onFailure: (error) => {
 *     // Same is true for onFailure callbacks
 *     return {
 *       type: 'GET_ARTICLES_FAILURE',
 *     }
 *   }
 * });
 * ```
 * 
 * @param {String} type - The redux action type prefix used for the created actions
 * @param {Object} opts - Options for `createHttpAction`
 * @param {Function} opts.onSuccess - A custom callback to fire when a request is successful. Must return a valid redux action that has a type equal to `${type}_SUCCESS`
 * @param {Function} opts.onFailure - A custom callback to fire when a request fails. Must return a valid redux action that has a type equal to `${type}_FAILURE`
 *
 * @returns {Object} - An object with 4 actions outlined above.
 */


var createHttpAction = function createHttpAction(type, opts) {
  var _ref;

  if (opts === void 0) {
    opts = {};
  }

  var successAction = createAction(type + "_SUCCESS");
  var failureAction = createAction(type + "_FAILURE"); // DRF puts multiple items under a data key. If that's present, pull it out
  // automatically to have this work with the byId reducer without user config.

  var defaultOnSuccess = function defaultOnSuccess(response) {
    return response.data ? successAction(response.data) : successAction(response);
  };

  var onSuccess = opts.onSuccess || defaultOnSuccess;
  var onFailure = opts.onFailure || failureAction;
  onSuccess.toString = successAction.toString;
  onFailure.toString = failureAction.toString;
  var httpRequest = createAction("HTTP_" + type);
  var formattedType = formatActionName(type);
  return _ref = {}, _ref[formattedType + "Start"] = httpRequest, _ref[formattedType + "Success"] = onSuccess, _ref[formattedType + "Failure"] = onFailure, _ref[formattedType + "WithOptions"] = function WithOptions(options) {
    return httpRequest({
      onSuccess: onSuccess,
      onFailure: onFailure,
      options: options
    });
  }, _ref;
};

export default createHttpAction;